<template>
  <div
    class="Quantitybutton"
    :class="{
      hasError: props.hasError,
      hasSuccess: props.hasSuccess,
      isDisabled: attrs.disabled !== undefined && attrs.disabled !== false
    }"
  >
    <button class="decrease" :disabled="value <= attrs.min" aria-label="weniger" @click="value--">
      -
    </button>
    <input v-bind="attrs" :id="id" v-model="value" inputmode="numeric" @focus="showHint = true" @blur="showHint = false" />
    <button class="increase" :disabled="value >= attrs.max" aria-label="mehr" @click="value++">
      +
    </button>
    <label v-show="label" :for="id">{{ label }}</label>

    <div
      v-if="props.hasError && props.errorMessage && !props.hasSuccess && props.errorMessage!==''"
      class="text-danger text-s message-wrapper message-error"
    >
      {{ errorMessage }}
    </div>
    <div
      v-if="(($slots.hint && props.persistentHint) || ($slots.hint && !props.persistentHint && showHint)) && $slots.hint!==''"
      class="text-s message-wrapper message-hint"
    >
      <slot name="hint"></slot>
    </div>
  </div>
</template>


<script setup>
import { useAttrs, ref, useId } from 'vue'

defineOptions({ inheritAttrs: false })

const value = defineModel({ type: Number, required: false, default: 0 })
const props = defineProps({
  label: {
    type: String,
    required: false,
    default: null,
  },
  hasError: {
    type: Boolean,
    required: false,
    default: false,
  },
  hasSuccess: {
    type: Boolean,
    required: false,
    default: false,
  },
  errorMessage: {
    type: String,
    required: false,
    default: null,
  },
  persistentHint: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const attrs = useAttrs()
const id = attrs.id ? attrs.id : useId()
const showHint = ref(false)
</script>


<style lang="scss" scoped>
.Quantitybutton {
  --borderColor: var(--blue);
  --inputSize: 2.25rem;
  --borderRadius: .25rem;
  display: grid;
  grid-template-columns: 1.5rem var(--inputSize) 1.5rem auto;
  grid-template-rows: 2rem auto auto;
  align-items: center;

  &.isDisabled,
  &:has(button[disabled]) {
    --borderColor: var(--transparent);
  }

  &.hasError {
    --borderColor: var(--danger);
  }

  &.hasSuccess {
    --borderColor: var(--success);
  }

  button {
    --backgroundColor: var(--blue);
    color: var(--white);
    padding: 0;
    cursor: pointer;
    display: grid;
    place-content: center;
    font-family: inherit;
    touch-action: manipulation;
  }

  button.decrease {
    border-top-left-radius: var(--borderRadius);
    border-bottom-left-radius: var(--borderRadius);
  }

  button.increase {
    border-top-right-radius: var(--borderRadius);
    border-bottom-right-radius: var(--borderRadius);
  }

  button[disabled],
  &.isDisabled :is(button, input) {
    --backgroundColor: var(--neutral-30);
    pointer-events: none;
  }

  input {
    --backgroundColor: var(--white);
    text-align: center;
    font-family: inherit;
  }

  &.isDisabled input {
    color: var(--white)
  }

  &.isDisabled input,
  input[readonly] {
    outline: none;
    pointer-events: none;
  }

  :is(button, input) {
    height: 100%;
    font-weight: 700;
    border: 1px solid var(--borderColor);
    background-color: var(--backgroundColor);
  }

  label {
    padding-inline-start: .5em;
  }

  .message-wrapper {
    grid-column: 1 / 5;
    padding-block-start: .25rem;

    > :first-child {
      margin-top: 0;
    }

    > :last-child{
      margin-bottom: 0;
    }
  }
}
</style>
